import React, { useState, useRef, useEffect, useCallback } from "react";

export default function MCarousel({ images = [], height = 400 }) {
	const [index, setIndex] = useState(0);
	const [containerWidth, setContainerWidth] = useState(0);

	// التحكم في تفعيل وإلغاء الانيميشن أثناء السحب لتجنب التقطيع
	const [isAnimating, setIsAnimating] = useState(true);

	// المراجع (Refs) للوصول لعناصر DOM
	const wrapperRef = useRef(null);
	const slidesRef = useRef(null);

	// متغيرات السحب (Drag State)
	const startX = useRef(0);
	const dragOffset = useRef(0);
	const isDragging = useRef(false);

	// === منطق التنقل (Navigation) ===
	const goTo = useCallback(
		(i) => {
			if (!images || images.length === 0) return;
			const newIndex = ((i % images.length) + images.length) % images.length;

			setIsAnimating(true); // تأكد من تفعيل الحركة عند التنقل بالأزرار
			setIndex(newIndex);
		},
		[images]
	);

	const next = useCallback(() => goTo(index + 1), [index, goTo]);
	const prev = useCallback(() => goTo(index - 1), [index, goTo]);

	// === قياس العرض (Width Measurement) ===
	useEffect(() => {
		// دالة القياس
		const measure = () => {
			if (wrapperRef.current) {
				const w = wrapperRef.current.offsetWidth;
				// تحديث العرض فقط إذا تغير لتجنب render زائد
				if (w > 0 && w !== containerWidth) {
					setContainerWidth(w);
				}
			}
		};

		// 1. قياس فوري
		measure();

		// 2. مراقبة تغيير حجم العنصر
		const ro = new ResizeObserver(() => measure());
		if (wrapperRef.current) ro.observe(wrapperRef.current);

		// 3. مراقبة تغيير حجم النافذة كإجراء احتياطي
		window.addEventListener("resize", measure);

		// تنظيف الذاكرة
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [containerWidth]); // الاعتماد على containerWidth لمنع التكرار غير الضروري

	// إعادة ضبط المؤشر إذا تغيرت الصور
	useEffect(() => {
		if (!images || images.length === 0) {
			setIndex(0);
		} else {
			setIndex((i) => Math.max(0, Math.min(i, images.length - 1)));
		}
	}, [images]);

	// حساب الإزاحة الأساسية بناءً على المؤشر الحالي
	const baseTranslateX = -index * containerWidth;

	// === منطق السحب (Drag Logic) ===

	const handleMove = useCallback(
		(currentX) => {
			if (!isDragging.current || !slidesRef.current) return;
			dragOffset.current = currentX - startX.current;

			// تحريك العنصر مباشرة (Direct DOM Manipulation) لأداء عالٍ
			slidesRef.current.style.transform = `translateX(${baseTranslateX + dragOffset.current}px)`;
		},
		[baseTranslateX]
	);

	const handleEnd = useCallback(() => {
		if (!isDragging.current || !slidesRef.current) return;

		// 1. إعادة تفعيل الانيميشن ليتم "جذب" الشريحة لمكانها بسلاسة
		setIsAnimating(true);

		// 2. تحديد هل تم السحب مسافة كافية للانتقال؟
		const threshold = Math.max(30, containerWidth * 0.15); // عتبة السحب (15% من العرض أو 30px)
		if (dragOffset.current < -threshold) next();
		else if (dragOffset.current > threshold) prev();
		else {
			// إذا لم تكن المسافة كافية، عد إلى المكان الأصلي (سيتم ذلك عبر إعادة render بالانيميشن)
			// لكن نحتاج لتحديث الستايل يدوياً هنا في حال لم يتغير الـ index
			slidesRef.current.style.transform = `translateX(${baseTranslateX}px)`;
		}

		// 3. تصفير حالة السحب
		isDragging.current = false;
		dragOffset.current = 0;
	}, [containerWidth, next, prev, baseTranslateX]);

	const handleStart = useCallback((clientX, e) => {
		if (!slidesRef.current) return;

		// 1. إيقاف الانيميشن فوراً لجعل السحب يستجيب لحركة اليد (بدون تأخير)
		setIsAnimating(false);

		// 2. تسجيل نقطة البداية
		startX.current = clientX;
		dragOffset.current = 0;
		isDragging.current = true;
	}, []);

	// Mouse Handlers
	const onMouseDown = (e) => {
		e.preventDefault();
		handleStart(e.clientX, e);
	};
	const onMouseMove = (e) => handleMove(e.clientX);
	const onMouseUp = handleEnd;
	const onMouseLeave = () => isDragging.current && handleEnd();

	// Touch Handlers
	const onTouchStart = (e) => {
		if (e.touches?.length) handleStart(e.touches[0].clientX, e);
	};
	const onTouchMove = (e) => {
		if (e.touches?.length) handleMove(e.touches[0].clientX);
	};
	const onTouchEnd = handleEnd;

	// Keyboard Navigation
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "ArrowRight") next();
			if (e.key === "ArrowLeft") prev();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [next, prev]);

	// === Render ===

	// حساب الستايل النهائي: هل نستخدم القيمة اليدوية (أثناء السحب) أم المحسوبة (أثناء الثبات)؟
	const transformStyle =
		isDragging.current && slidesRef.current
			? slidesRef.current.style.transform // أثناء السحب نعتمد على التحديث المباشر
			: `translateX(${baseTranslateX}px)`; // أثناء الثبات نعتمد على الـ state

	if (!images || images.length === 0) {
		return (
			<div
				className="relative rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 border border-gray-300"
				style={{ height }}
			>
				No images available
			</div>
		);
	}

	return (
		<div
			ref={wrapperRef}
			className="relative overflow-hidden rounded-xl select-none w-full cursor-grab active:cursor-grabbing bg-gray-900"
			style={{ height, touchAction: "pan-y" }}
			// Attach event listeners
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onMouseDown={onMouseDown}
			onMouseMove={onMouseMove}
			onMouseUp={onMouseUp}
			onMouseLeave={onMouseLeave}
			dir="ltr" // مهم جداً: لضمان أن الحسابات تعمل بشكل صحيح دائماً من اليسار لليمين
		>
			{/* Slides wrapper */}
			<div
				ref={slidesRef}
				className={`flex h-full ${isAnimating ? "transition-transform duration-500 ease-out" : ""}`}
				style={{
					width: containerWidth ? `${images.length * containerWidth}px` : "100%", // عرض افتراضي حتى يتم القياس
					transform: transformStyle,
					opacity: containerWidth ? 1 : 0, // إخفاء المحتوى لحظياً حتى يتم حساب العرض لمنع الوميض
					transition: "opacity 0.2s",
				}}
			>
				{images.map((img, i) => (
					<div key={i} className="flex-shrink-0 relative" style={{ width: containerWidth || "100%", height: "100%" }}>
						<img
							src={img}
							alt={`Slide ${i + 1}`}
							loading="lazy"
							draggable={false} // منع سحب الصورة نفسها كملف
							onError={(ev) => {
								const parent = ev.currentTarget.parentElement;
								if (parent) {
									ev.currentTarget.style.display = "none";
									// إضافة خلفية في حال فشل الصورة
									const fallback = document.createElement("div");
									fallback.className = "w-full h-full flex items-center justify-center bg-gray-800 text-gray-400";
									fallback.innerText = "Image failed";
									parent.appendChild(fallback);
								}
							}}
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								display: "block",
								pointerEvents: "none", // يمنع تداخل أحداث الماوس مع الصورة
							}}
						/>
					</div>
				))}
			</div>

			{/* Navigation Buttons */}
			{images.length > 1 && (
				<>
					<button
						onClick={(e) => {
							e.stopPropagation();
							prev();
						}}
						className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60 z-10"
						aria-label="Previous"
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
						</svg>
					</button>

					<button
						onClick={(e) => {
							e.stopPropagation();
							next();
						}}
						className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white p-2 rounded-full hover:bg-black/60 z-10"
						aria-label="Next"
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>

					{/* Dots */}
					<div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
						{images.map((_, i) => (
							<button
								key={i}
								onClick={(e) => {
									e.stopPropagation();
									goTo(i);
								}}
								className={`w-2.5 h-2.5 rounded-full transition-all ${
									index === i ? "bg-white w-6" : "bg-white/50 hover:bg-white/80"
								}`}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}
